import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { findBuyCatalogProduct } from '../../components/GTT/Common/findBuyCatalogProduct';
import { ENTRY_POINTS } from '../../components/GTT/Common/gtmEventHelper';
import {
  GTT_TARGET_PRICE,
  GTT_TARGET_QUANTITY,
  TRIGGER_ORDER_WIZARD,
  WIZARD_DATA,
} from '../../constants/sessionStorage';
import { fetchProductsWithPrices } from '../../store/actions/products/productActions';
import { PRODUCT_PRICES_LOADING } from '../../store/actions/products/productActionConstants';
import { TARGET_PRICE_AGAIN_STATUS } from '../../store/actions/triggerOrders/triggerOrdersActionConstants';
import { finishTargetPriceAgain } from '../../store/actions/triggerOrders/triggerOrdersActions';
import { getCurrentAccountUid } from '../../store/selectors/accountSelectors';
import { selectIsLoadingByActionType } from '../../store/selectors/loadingSelectors';
import { selectProductPrices } from '../../store/selectors/productsSelector';
import { selectTargetPriceAgain } from '../../store/selectors/triggerOrdersSelectors';
import { productLocationTypesByName } from '../enum/api/productTypes';
import {
  removeFromSessionStorage,
  storeInSessionStorage,
} from '../helpers/sessionStorageHelper';
import { setWizardContent } from '../helpers/wizardHelpers';

// The order-entry step of the Target Price flow.
const ORDER_ENTRY_STEP = 1;

// How long a request is honoured, which is also as long as the details modal
// spins for it: one still waiting on the product prices or the catalog lookup
// after this is dropped, and not opened out of the blue when they finally land.
const REQUEST_MAX_AGE_MS = 20 * 1000;

const isFresh = (request) =>
  Date.now() - request.requestedAt <= REQUEST_MAX_AGE_MS;

// The vault the expired order was in, as the vault the flow keeps in its wizard
// data: the product's own location of that type (it carries the SymbolCode and
// prices the flow matches on) and, for a segregated order, the premium plan when
// that location offers it. Nothing is preselected when the product no longer has
// that vault, or for Delivery, which Target Price Orders don't support: Order
// Entry then offers its usual default.
const getOrderVault = (product, { locationType, isSegregated }) => {
  const vault = (product.Locations || []).find(
    (location) =>
      location.LocationType === locationType &&
      location.LocationType !== productLocationTypesByName.Delivery,
  );

  if (!vault) {
    return {};
  }

  return {
    selectedVault: vault,
    selectedPlan:
      isSegregated && vault.IsAvailableForSegregated ? 'premium' : 'standard',
  };
};

// Opens the Target Price flow on Order Entry for an expired order's product and
// vault, wherever the user is: the "Set target price again" button of the order's
// details (TriggerOrderDetailsModal) only files a request, and this, mounted once
// by the Header (which hosts the flow on every page), carries it out. The product
// (the Algolia hit merged with its price record, which the details page doesn't
// have loaded) is looked up in the buy catalog, loading the prices first when the
// store has none.
//
// The request stays pending until the flow is open, which is what the details
// modal shows its spinner for, and is then finished with how it ended: opened, or
// blocked (an ineligible account gets the verification popup instead, like the
// other entry points). It is dropped, not retried, when the feature is off or the
// account can't create Target Price Orders, when it has gone stale (the prices
// never loaded), when the product isn't in the catalog any more, and when a
// lookup fails.
//
// isEnabled: the feature is on and the account has Target Price Order entry.
// blockIfIneligible: useTriggerOrderVerification's, true when it blocked.
// openFlow: shows the flow, once its session data is in place.
const useTargetPriceAgain = ({ isEnabled, blockIfIneligible, openFlow }) => {
  const dispatch = useDispatch();
  const request = useSelector(selectTargetPriceAgain);
  const productPrices = useSelector(selectProductPrices);
  const isLoadingPrices = useSelector(
    selectIsLoadingByActionType(PRODUCT_PRICES_LOADING),
  );
  const accountUid = useSelector(getCurrentAccountUid);
  // The request the prices were asked for, and the one whose product is being
  // looked up, so each is done once however often this runs while it waits.
  const pricesAskedFor = useRef(null);
  const lookupStartedFor = useRef(null);
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  // However it stalls (the prices or the lookup fail, or hang), a pending request
  // ends once it is no longer fresh, so the modal's spinner can't spin for good.
  useEffect(() => {
    if (request?.status !== TARGET_PRICE_AGAIN_STATUS.PENDING) {
      return undefined;
    }

    const timeout = setTimeout(
      () => dispatch(finishTargetPriceAgain(TARGET_PRICE_AGAIN_STATUS.DROPPED)),
      Math.max(0, request.requestedAt + REQUEST_MAX_AGE_MS - Date.now()),
    );

    return () => clearTimeout(timeout);
  }, [request, dispatch]);

  useEffect(() => {
    if (request?.status !== TARGET_PRICE_AGAIN_STATUS.PENDING) return;

    const finish = (status) => dispatch(finishTargetPriceAgain(status));

    if (!isEnabled || !isFresh(request)) {
      finish(TARGET_PRICE_AGAIN_STATUS.DROPPED);
      return;
    }

    if (blockIfIneligible()) {
      finish(TARGET_PRICE_AGAIN_STATUS.BLOCKED);
      return;
    }

    // No prices yet: ask for them and wait for them to land (the request carries
    // on from here when they do). Not for as long as the loading flag says, which
    // is cleared just before the prices are stored, so an empty list with nothing
    // loading doesn't mean the answer was empty. A request that never gets its
    // prices simply goes stale.
    if (!productPrices?.length) {
      if (
        isLoadingPrices ||
        pricesAskedFor.current === request ||
        !accountUid
      ) {
        return;
      }

      pricesAskedFor.current = request;
      dispatch(fetchProductsWithPrices({ accountUid, side: 'Buy' }));
      return;
    }

    if (lookupStartedFor.current === request) return;
    lookupStartedFor.current = request;

    findBuyCatalogProduct(request.productCode, productPrices)
      .then((product) => {
        if (!isMounted.current) return;

        // No product, or it took so long the request has already been dropped.
        if (!product || !isFresh(request)) {
          finish(TARGET_PRICE_AGAIN_STATUS.DROPPED);
          return;
        }

        // A fresh journey for this product that skips product selection (which
        // is what normally drops a target price and quantity left over from an
        // earlier session), so clear them here or they leak into Order Entry.
        removeFromSessionStorage(GTT_TARGET_PRICE);
        removeFromSessionStorage(GTT_TARGET_QUANTITY);
        storeInSessionStorage(WIZARD_DATA, {
          entryPoint: ENTRY_POINTS.EXPIRED_ORDER,
          selectedProduct: product,
          ...getOrderVault(product, request),
        });
        setWizardContent(TRIGGER_ORDER_WIZARD, ORDER_ENTRY_STEP);
        openFlow();
        finish(TARGET_PRICE_AGAIN_STATUS.OPENED);
      })
      .catch(() => {
        if (isMounted.current) finish(TARGET_PRICE_AGAIN_STATUS.DROPPED);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request, productPrices, isLoadingPrices]);
};

export default useTargetPriceAgain;
